import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import {
  PlayCircleOutlined,
  ImportOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CopyOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
} from "us.icons";
import {
  $Button,
  $Tooltip,
  $Popconfirm,
  $Input,
  $Select,
  $Message,
  $Popover,
  $MessageBox,
} from "us.common/components";
import "./HeaderExtra.scss";
import { RootState } from "us.helper/types";
import * as Actions from "actions";
import { URLParams } from "components/EditorHome/Constants";
import {
  generateTemplateJSON,
  downloadJsonFile,
} from "components/TemplateEditor/Functions";
import { generatePreviewImage } from "functions/TemplateEditor";
import { DocumentTemplates, DocumentType } from "constants/TemplateEditor";
import "./USCMenu.scss";
import _ from "lodash";
import { getUrlParams } from "components/EditorHome/Functions";
import { Guid } from "guid-typescript";
import {
  isTemplateNameChanged,
  isValidTemplate,
  isValidTemplateFile,
} from "./Functions";
import { SaveAs } from "./Components/USCMenu/Components/Content/Components";
import { getCurrentUserEmail } from "us.helper/utility";
import * as Azure from "us.helper/azure";
import { getUpdatedVersion } from "functions/TemplateHistory";
import { getTemplateAdapterIds } from "functions";
import { useLocalTemplate } from "hooks";
import { TemplateVersion } from "components/TemplateEditor/Components/VersionHistory/Constants";

const HeaderExtra: React.FC<PropsFromRedux> = (props) => {
  const {
    template,
    documentTypes,
    templates,
    templateVersions,
    AuthorizationProfile,
    importTemplate,
    setProperties,
    getDocumentTypes,
    saveTemplate,
    getTemplateDetails,
    updateTemplate,
    getParameters,
    resetTemplatePreview,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const { categoryId, groupId, templateId, documentType } =
    getUrlParams(search);

  const { saveLocalTemplate, getLocalTemplate } = useLocalTemplate();

  const [popoverStatus, setPopoverStatus] = useState(false);
  const [templateFile, setTemplateFile] = useState<any>({});
  const [categoryName, setCategoryName] = useState<string>("");
  const [groupName, setGroupName] = useState<string>("");

  const isXML = documentType == DocumentType.EHF;

  const { mainAdapterId } = template?.data?.templateContent?.adapter ?? {};
  const { categoryId: templateCategoryId } = template?.data ?? {};
  const [saveAsVisible, setSaveAsVisible] = useState<boolean>(false);

  useEffect(() => {
    if (template?.newTemplateId == -1) {
      const templateData = getLocalTemplate();
      if (templateData) {
        setGroupName(templateData.groupName);
        setCategoryName(templateData.categoryName);
        updateTemplate && updateTemplate(templateData);
        const adapterIds = getTemplateAdapterIds({
          template: templateData,
          all: true,
        });
        getParameters && getParameters(adapterIds);
      } else if (template.data) {
        setGroupName(template.data.groupName || "");
        setCategoryName(template.data.categoryName || "");
        saveLocalTemplate(template.data);
      }
    }
    getDocumentTypes && getDocumentTypes({});
  }, []);

  useEffect(() => {
    if (template?.isSaveAs && template?.newTemplateId > 0) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("id", template?.newTemplateId?.toString());
      navigate(`${location.pathname}?${searchParams.toString()}`);
    }
    if (template.data && template.data.templateId > 0) {
      setGroupName(template.data.groupName);
      setCategoryName(template.data.categoryName);
      setSaveAsVisible(false);
    }
  }, [template]);

  const openPreview = () => {
    $MessageBox(
      "warning",
      `US.USC.TEMPLATEEDITOR:MESSAGES.MAKE_SURE_YOU_HAVE_SAVED_YOUR_RECENT_CHANGES_TO_REFLECT_THEM_IN_THE_PREVIEW`,
      "",
      ""
    );
    resetTemplatePreview && resetTemplatePreview({});
    const current = new URLSearchParams(search);
    current.set("preview", "true");
    navigate({ search: current.toString() }, { replace: true });
  };

  const updatedUser = useMemo(() => {
    const currentUser: any = new Azure.ADAuth().currentUser();
    return getCurrentUserEmail(
      currentUser,
      AuthorizationProfile?.authorizationProfile
    );
  }, [AuthorizationProfile]);

  const getVersionNo = useCallback(
    (versions: Array<TemplateVersion>, template: any) => {
      return getUpdatedVersion(versions, template);
    },
    [template, templateVersions]
  );

  /**
   * @description - Get extension from a file name
   * @param {string} filename - Name of the file
   * @returns - Extension of the given filename
   */
  const getExtension = (filename: string): string => {
    try {
      var parts = filename.split(".");
      return parts[parts.length - 1];
    } catch (error) {
      return "";
    }
  };

  /**
   *@description - Handle file selection and import
   */
  const handleFileImport = (e: any) => {
    const file = e.target.files[0];
    const extension = getExtension(file.name);
    if (file && extension === "json") {
      const fileReader = new FileReader();
      fileReader.readAsText(file, "UTF-8");
      fileReader.onload = (e) => {
        const result = e?.target?.result;
        if (result) {
          setTemplateFile(JSON.parse(result.toString()));
        }
      };
    }
  };

  /**
   * @description - Handle to load the opened template file
   */
  const handleOpenTemplate = () => {
    if (templateFile) {
      importTemplate &&
        importTemplate({
          ...templateFile,
          templateId: 0,
          templateContent: {
            ...templateFile?.templateContent,
            id: 0,
            batchNo: Guid.create().toString(),
          },
        });
      setPopoverStatus(false);
    }
  };

  /**
   * @description - Handle template name change event
   */
  const handleSetName = (e: any, setFieldValue: any) => {
    const templateName = e.target?.value;
    if (templateName) {
      const templateChanges = {
        templateName,
        templateContent: {
          ...template.data.templateContent,
          templateName,
        },
      };
      setProperties && setProperties(templateChanges);
      saveLocalTemplate({
        ...template.data,
        ...templateChanges,
      });
    } else {
      setFieldValue("templateName", template.data?.templateName);
    }
  };

  /**
   * @description - Handle save template button click event
   */
  const onSaveTemplate = async () => {
    if (isXML) {
      saveTemplate &&
        saveTemplate({
          saveParams: {
            ...template.data,
            templateContent: {
              ...template.data?.templateContent,
              versionNo: getVersionNo(templateVersions?.data, template.data),
              updatedUser,
            },
            isTemplateNameChanged: isTemplateNameChanged(
              template?.data,
              templates?.data
            ),
          },
        });
    } else {
      const templateJson = generateTemplateJSON(template.data);
      if (templateJson && isValidTemplate()) {
        const parsedTemplateJson = JSON.parse(templateJson);
        try {
          const previewImage = await generatePreviewImage();
          const versionNo = getVersionNo(
            templateVersions?.data,
            parsedTemplateJson
          );
          saveTemplate &&
            saveTemplate({
              saveParams: {
                ...parsedTemplateJson,
                templateContent: {
                  ...parsedTemplateJson?.templateContent,
                  previewImage,
                  versionNo,
                  updatedUser,
                },
                isTemplateNameChanged: isTemplateNameChanged(
                  template?.data,
                  templates?.data
                ),
              },
              isSaveAs: true,
            });
        } catch (e) {
          saveTemplate &&
            saveTemplate({
              saveParams: {
                ...parsedTemplateJson,
                templateContent: {
                  ...parsedTemplateJson?.templateContent,
                  versionNo: 1,
                  updatedUser,
                },
                isTemplateNameChanged: false,
              },
            });
        }
      } else {
        $Message.error(
          t(
            "US.USC.TEMPLATEEDITOR:VALIDATIONS.PLEASE_SET_UP_THE_REPEATING_ADAPTER_TO_YOUR_TEMPLATE_FOR_YOUR_REPEATING_TOKENS"
          )
        );
      }
    }
  };

  /**
   * @description - Export current opened template as a json file
   */
  const exportJSON = () => {
    if (template.data) {
      if (isXML) {
        //TODO
      } else {
        const json = generateTemplateJSON(template.data);
        downloadJsonFile(json, template.data?.templateName);
      }
    }
  };

  /**
   * @description - Refresh template data by calling the API again
   */
  const handleRefresh = () => {
    if (templateId || (template && template.data.templateId)) {
      getTemplateDetails &&
        getTemplateDetails({
          templateId: template?.data?.templateId ?? templateId,
          version: 0,
        });
    }
  };

  /**
   * Handles the change of document type.
   * @param {DocumentType} value - The change event from the document type select element.
   */
  const handleDocumentTypeChange = (value: DocumentType) => {
    urlParams.set(URLParams.DOCUMENT_TYPE, value);
    navigate({ search: urlParams.toString() }, { replace: true });
    const updatedTemplate = {
      ...template.data,
      documentType: value,
      templateContent: {
        ...template.data.templateContent,
        pages: DocumentTemplates[value].templateContent.pages,
        output: { format: value },
      },
    };
    setProperties && setProperties(updatedTemplate);
    saveLocalTemplate(updatedTemplate);
  };

  return (
    <Formik
      initialValues={{
        templateName: template.data?.templateName,
        documentType,
      }}
      enableReinitialize
      onSubmit={() => {}}
    >
      {({ values, setFieldValue }) => (
        <>
          <div className="usc-header">
            <$Tooltip
              placement="bottom"
              title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.BACK_TO_HOME")}
            >
              <div
                hidden={pathname == "/"}
                className="icon-header-btn home-btn mr-3"
              >
                <$Button
                  icon={<ArrowLeftOutlined />}
                  type="link"
                  size="large"
                  className="px-3"
                  onClick={() =>
                    // push(
                    //   `/?${URLParams.GROUP_ID}=${groupId}&categoryId=${categoryId}`
                    // )
                    navigate(
                      `/?${URLParams.GROUP_ID}=${groupId}&categoryId=${categoryId}`
                    )
                  }
                  style={{ width: "auto" }}
                />
              </div>
            </$Tooltip>

            <$Popconfirm
              title={t("COMMON.SURE_TO_PROCEED_?")}
              placement="topLeft"
              data-testid="pop-confirm"
              okButtonProps={{
                dataTestid: "pop-confirm-ok",
              }}
              onConfirm={onSaveTemplate}
              okText={t("COMMON.YES")}
              cancelText={t("COMMON.NO")}
              disabled={!template?.data}
            >
              <$Tooltip placement="bottom" title={t("COMMON.SAVE")}>
                <div hidden={pathname == "/"} className="icon-header-btn">
                  <$Button
                    icon={<SaveOutlined />}
                    type="link"
                    size="large"
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
            </$Popconfirm>

            {!isXML && (
              <div hidden={pathname == "/"} className="icon-header-btn">
                <$Popover
                  trigger="click"
                  placement="topLeft"
                  content={<SaveAs />}
                  destroyTooltipOnHide
                  visible={saveAsVisible}
                  onVisibleChange={(visible: boolean) => {
                    setSaveAsVisible(visible);
                  }}
                  overlayClassName="usc-save-as-popover"
                >
                  <$Tooltip
                    placement="bottom"
                    title={t("US.COMMON:COMMON.SAVE_AS")}
                  >
                    <$Button
                      icon={<CopyOutlined />}
                      type="link"
                      size="large"
                      disabled={!template?.data}
                    />
                  </$Tooltip>
                </$Popover>
              </div>
            )}

            {!isXML && (
              <$Popconfirm
                title={
                  <div>
                    <h3>
                      {t(
                        "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.IMPORT_TEMPLATE"
                      )}
                    </h3>
                    <input
                      type="file"
                      onChange={handleFileImport}
                      accept="application/JSON"
                      className="mt-2"
                    />
                    <br />
                    {Object.entries(templateFile).length != 0 &&
                      isValidTemplateFile(
                        templateFile,
                        mainAdapterId,
                        templateCategoryId
                      ) && (
                        <span className="ant-form-item-explain-error">
                          {t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.INCOMPATIBLE_TEMPLATE_CONTENT"
                          )}
                        </span>
                      )}
                  </div>
                }
                icon={<></>}
                onVisibleChange={(visible: any) => {
                  if (!visible) {
                    setPopoverStatus(false);
                    setTemplateFile({});
                  }
                }}
                okButtonProps={{
                  disabled:
                    Object.entries(templateFile).length == 0 ||
                    templateFile?.templateContent?.adapter?.mainAdapterId !=
                      mainAdapterId ||
                    templateFile?.categoryId != templateCategoryId,
                }}
                destroyTooltipOnHide
                placement="topLeft"
                onConfirm={() => {
                  handleOpenTemplate();
                }}
                onCancel={() => {
                  setPopoverStatus(false);
                  setTemplateFile({});
                }}
                okText={"Open"}
                cancelText={t("COMMON.CANCEL")}
                open={popoverStatus} //replced not requested
                disabled={!template?.data}
              >
                <$Tooltip
                  placement="bottom"
                  title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.IMPORT")}
                >
                  <div hidden={pathname == "/"} className="icon-header-btn">
                    <$Button
                      icon={<ImportOutlined />}
                      type="link"
                      size="large"
                      onClick={() => setPopoverStatus(true)}
                      disabled={!template?.data}
                    />
                  </div>
                </$Tooltip>
              </$Popconfirm>
            )}

            {!isXML && (
              <$Tooltip
                placement="bottom"
                title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.EXPORT")}
              >
                <div hidden={pathname == "/"} className="icon-header-btn">
                  <$Button
                    icon={<DownloadOutlined />}
                    type="link"
                    size="large"
                    onClick={() => exportJSON()}
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
            )}

            {template?.data?.templateId > 0 && (
              <$Popconfirm
                title={
                  t("COMMON.YOUR_UNSAVED_CHANGES_WILL_BE_LOST") +
                  `, ${t("COMMON.SURE_TO_PROCEED_?")}`
                }
                placement="topLeft"
                data-testid="pop-confirm"
                okButtonProps={{
                  dataTestid: "pop-confirm-ok",
                }}
                onConfirm={handleRefresh}
                okText={t("COMMON.YES")}
                cancelText={t("COMMON.NO")}
              >
                <$Tooltip placement="bottom" title={t("COMMON.REFRESH")}>
                  <div hidden={pathname == "/"} className="icon-header-btn">
                    <$Button
                      icon={<ReloadOutlined />}
                      type="link"
                      size="large"
                    />
                  </div>
                </$Tooltip>
              </$Popconfirm>
            )}

            {!isXML && (
              <$Tooltip
                placement="bottom"
                title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PREVIEW")}
              >
                <div hidden={pathname == "/"} className="icon-header-btn">
                  <$Button
                    icon={<PlayCircleOutlined />}
                    type="link"
                    size="large"
                    onClick={() => openPreview()}
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
            )}

            <div className="usc-center-content">
              <div className="d-flex">
                {pathname !== "/" && (
                  <>
                    <div className="usc-type-select">
                      <$Select
                        name="documentType"
                        size="small"
                        className="mr-2"
                        value={documentType}
                        loading={documentTypes.loading}
                        disabled={
                          documentTypes.loading ||
                          template.data?.templateId > 0 ||
                          !template?.data
                        }
                        options={documentTypes.data}
                        optionText={"channelDisplayName"}
                        optionValue={"channelCode"}
                        onChange={handleDocumentTypeChange}
                        showSearch={false}
                        allOption={false}
                        dropdownMatchSelectWidth={false}
                      />
                    </div>
                    {groupName && (
                      <$Tooltip placement="bottom" title={groupName}>
                        <strong className="text-white text-truncate">
                          {groupName}
                        </strong>
                      </$Tooltip>
                    )}
                    {groupName && (
                      <strong className="text-white ml-1 mr-1">/</strong>
                    )}
                    {categoryName && (
                      <$Tooltip placement="bottom" title={categoryName}>
                        <strong className="text-white text-truncate">
                          {categoryName}
                        </strong>
                      </$Tooltip>
                    )}
                    {categoryName && (
                      <strong className="text-white mx-1">/</strong>
                    )}
                    <div className="auto-grow-input">
                      <span>
                        <$Input
                          name="templateName"
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleSetName(e, setFieldValue)
                          }
                          onPressEnter={(
                            e: React.KeyboardEvent<HTMLInputElement>
                          ) => handleSetName(e, setFieldValue)}
                          className="mt-2 pl-1 text-white font-weight-bold"
                          maxLength={250}
                          disabled={!template?.data}
                          onChange={(e: any) =>
                            setFieldValue("templateName", e.target.value)
                          }
                        />
                      </span>
                      <span>{values.templateName}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory, AuthorizationProfile } = state;
  const { section, parameters } = templateEditor;
  const { template, documentTypes, templates, templateVersions } =
    templatesHistory;

  return {
    section,
    parameters,
    template,
    documentTypes,
    templates,
    templateVersions,
    AuthorizationProfile,
  };
};

const { template, documentTypes, pages, templateData } =
  Actions.templatesHistory;
const { parameters, templatePreview } = Actions.templateEditor;

const mapDispatchToProps = {
  importTemplate: template.import,
  setProperties: pages.setProperties,
  getDocumentTypes: documentTypes.get,
  saveTemplate: templateData.save,
  getTemplateDetails: template.get,
  updateTemplate: template.update,
  getParameters: parameters.get,
  resetTemplatePreview: templatePreview.reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(HeaderExtra);
function replace(arg0: { search: string }) {
  throw new Error("Function not implemented.");
}
